import React, { useContext } from "react";

import { ImageOptions } from "@/common/Options/ImageOptions";
import { TextOptions } from "@/common/Options/TextOptions";
import { FormButton } from "@/components/Form";
import {
  OptionEnum,
  OptionModeEnum,
  OptionTypeEnum,
} from "@/components/Option";
import { getPollOptionVotesPercent, PollContext } from "@/contexts";
import LocaleContext from "@/i18n";
import {
  InteractionLayout,
  Option,
  PollResult,
  PollVote,
  Slide,
} from "@/service/Model";

export type PollOptionsProps = {
  interactionId: string;
  slide: Slide;
  results: PollResult[];
  isSecret?: boolean;
  isEnded?: boolean;
  isFinished: boolean;
  layout?: InteractionLayout;
  userVotes: PollVote[];
  onNext: (timeout: number, branch?: string) => void;
};

export const PollOptions: React.FC<PollOptionsProps> = (
  props: PollOptionsProps
): JSX.Element => {
  const pollContext = useContext(PollContext);
  const localeContext = useContext(LocaleContext);

  const {
    layout,
    results,
    slide,
    interactionId,
    userVotes,
    isSecret,
    isEnded,
    isFinished,
    onNext,
  } = props;

  const hasSelectedOptions = userVotes.length > 0;

  const getOptionMode = (): OptionModeEnum =>
    isEnded || hasSelectedOptions
      ? OptionModeEnum.results
      : OptionModeEnum.normal;

  const getPercentage = (optionId: string, isUserAnswer: boolean): number => {
    if (isEnded || isFinished) {
      return getPollOptionVotesPercent(
        results,
        optionId,
        isFinished,
        isUserAnswer
      );
    }

    return 0;
  };

  const handleOnClick = (vote: PollVote, branch?: string): void => {
    if (isEnded || !pollContext) {
      return;
    }

    if (slide.multipleChoice) {
      pollContext.onSelect(vote);
    } else {
      pollContext.onVote(vote);
      onNext(2500, branch);
    }
  };

  const handleOnNext = (): void => {
    if (isEnded || !pollContext) {
      return;
    }
    pollContext.onConfirmSelection();
    onNext(0);
  };

  const resolveOption = (option: Option): OptionEnum | undefined => {
    if (userVotes.length) {
      const isUserAnswer = !!userVotes.find(
        (vote: PollVote) => vote.optionId === option.id
      );
      return isUserAnswer ? OptionEnum.vote : undefined;
    }

    return undefined;
  };

  const resolveOptionMode = (option: Option): OptionModeEnum => {
    const isUserAnswer = !!userVotes.find(
      (vote: PollVote) => vote.optionId === option.id
    );

    // Overwrite with "normal" mode if multipleChoice is configured
    return slide.multipleChoice && !isUserAnswer
      ? OptionModeEnum.normal
      : getOptionMode();
  };

  const resolvePercent = (option: Option): number | undefined => {
    const isUserAnswer = !!userVotes.find(
      (vote: PollVote) => vote.optionId === option.id
    );

    return getPercentage(option.id, isUserAnswer);
  };

  const options: JSX.Element =
    layout === InteractionLayout.Text ? (
      <TextOptions
        type={OptionTypeEnum.poll}
        interactionId={interactionId}
        slideId={slide.id}
        options={slide.options}
        onVote={handleOnClick}
        resolveOption={resolveOption}
        resolveOptionMode={resolveOptionMode}
        resolvePercent={resolvePercent}
        isClickable={!isFinished && !isEnded}
        isSecret={isSecret || slide.multipleChoice}
      />
    ) : (
      <ImageOptions
        type={OptionTypeEnum.poll}
        interactionId={interactionId}
        slideId={slide.id}
        options={slide.options}
        onVote={handleOnClick}
        resolveOption={resolveOption}
        resolveOptionMode={resolveOptionMode}
        resolvePercent={resolvePercent}
        isClickable={!isFinished && !isEnded}
        isSecret={isSecret || slide.multipleChoice}
      />
    );

  return (
    <React.Fragment>
      {options}
      {slide.multipleChoice && (
        <FormButton
          fullWidth={true}
          disabled={isFinished || userVotes.length === 0}
          onClick={handleOnNext}
          label={localeContext.translation.next}
        />
      )}
    </React.Fragment>
  );
};
